import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export interface PatronPeriodo {
  diaInicio: number;  // Día del mes de inicio (ej: 26, 1, 2)
  diaFin: number;     // Día del mes de fin (ej: 25, 30, 1)
  mesesOffset: number; // Diferencia de meses entre inicio y fin (0 = mismo mes, 1 = mes siguiente)
}

interface PeriodoContextType {
  // Período HOME (26 al 25)
  fechaInicio: string;
  fechaFin: string;
  setPeriodo: (fechaInicio: string, fechaFin: string) => void;

  // Período CALL CENTER (01 al 30/31)
  fechaInicioCallCenter: string;
  fechaFinCallCenter: string;
  setPeriodoCallCenter: (fechaInicio: string, fechaFin: string) => void;

  // Patrones personalizados de periodos
  patronPersonalizadoHome: PatronPeriodo | null;
  patronPersonalizadoCallCenter: PatronPeriodo | null;

  // Guardar patrón personalizado
  guardarPatronHome: (patron: PatronPeriodo) => void;
  guardarPatronCallCenter: (patron: PatronPeriodo) => void;

  // Eliminar patrón personalizado
  eliminarPatronHome: () => void;
  eliminarPatronCallCenter: () => void;

  // Run ID compartido entre todos los tabs para el mismo período
  currentRunId: number | undefined;
  setCurrentRunId: (runId: number | undefined) => void;

  // Task ID para seguimiento del cálculo en progreso
  taskId: string | null;
  setTaskId: (taskId: string | null) => void;

  // Estado de cálculo
  isCalculating: boolean;
  setIsCalculating: (isCalculating: boolean) => void;

  // Canales compartidos que se usaron en el último cálculo
  canalesCalculados: string[];
  setCanalesCalculados: (canales: string[]) => void;
}

const PeriodoContext = createContext<PeriodoContextType | undefined>(undefined);

/**
 * Genera el período HOME por defecto (del día 26 del mes anterior al 25 del mes actual)
 */
const generarPeriodoHomePorDefecto = () => {
  const hoy = new Date();

  // Período anterior (del día 26 del mes pasado al 25 de este mes)
  const mesInicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 26);
  const mesFin = new Date(mesInicio.getFullYear(), mesInicio.getMonth() + 1, 25);

  return {
    fechaInicio: mesInicio.toISOString().split('T')[0],
    fechaFin: mesFin.toISOString().split('T')[0],
  };
};

/**
 * Genera el período CALL CENTER por defecto (del día 01 al último día del mes anterior)
 */
const generarPeriodoCallCenterPorDefecto = () => {
  const hoy = new Date();

  // Período anterior (del día 01 al último día del mes pasado)
  const mesInicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  // El día 0 del mes siguiente es el último día del mes actual
  const mesFin = new Date(mesInicio.getFullYear(), mesInicio.getMonth() + 1, 0);

  return {
    fechaInicio: mesInicio.toISOString().split('T')[0],
    fechaFin: mesFin.toISOString().split('T')[0],
  };
};

const STORAGE_KEY_PATRON_HOME = 'comisiones_patron_home';
const STORAGE_KEY_PATRON_CALL_CENTER = 'comisiones_patron_call_center';

export function PeriodoProvider({ children }: { children: ReactNode }) {
  const periodoHomeDefault = generarPeriodoHomePorDefecto();
  const periodoCallCenterDefault = generarPeriodoCallCenterPorDefecto();

  // Función para cargar patrón desde localStorage
  const cargarPatron = (key: string): PatronPeriodo | null => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored) as PatronPeriodo;
      }
    } catch (error) {
      console.error('Error cargando patrón desde localStorage:', error);
    }
    return null;
  };

  // Estado para período HOME actual
  const [fechaInicio, setFechaInicio] = useState(periodoHomeDefault.fechaInicio);
  const [fechaFin, setFechaFin] = useState(periodoHomeDefault.fechaFin);

  // Estado para período CALL CENTER actual
  const [fechaInicioCallCenter, setFechaInicioCallCenter] = useState(
    periodoCallCenterDefault.fechaInicio
  );
  const [fechaFinCallCenter, setFechaFinCallCenter] = useState(
    periodoCallCenterDefault.fechaFin
  );

  // Patrones personalizados guardados
  const [patronPersonalizadoHome, setPatronPersonalizadoHome] = useState<PatronPeriodo | null>(
    () => cargarPatron(STORAGE_KEY_PATRON_HOME)
  );
  const [patronPersonalizadoCallCenter, setPatronPersonalizadoCallCenter] = useState<PatronPeriodo | null>(
    () => cargarPatron(STORAGE_KEY_PATRON_CALL_CENTER)
  );

  // Estado compartido del run_id y cálculo
  const [currentRunId, setCurrentRunId] = useState<number | undefined>(undefined);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [canalesCalculados, setCanalesCalculados] = useState<string[]>([]);

  // Guardar patrones en localStorage cuando cambien
  useEffect(() => {
    try {
      if (patronPersonalizadoHome) {
        localStorage.setItem(STORAGE_KEY_PATRON_HOME, JSON.stringify(patronPersonalizadoHome));
      } else {
        localStorage.removeItem(STORAGE_KEY_PATRON_HOME);
      }
    } catch (error) {
      console.error('Error guardando patrón HOME:', error);
    }
  }, [patronPersonalizadoHome]);

  useEffect(() => {
    try {
      if (patronPersonalizadoCallCenter) {
        localStorage.setItem(STORAGE_KEY_PATRON_CALL_CENTER, JSON.stringify(patronPersonalizadoCallCenter));
      } else {
        localStorage.removeItem(STORAGE_KEY_PATRON_CALL_CENTER);
      }
    } catch (error) {
      console.error('Error guardando patrón CALL CENTER:', error);
    }
  }, [patronPersonalizadoCallCenter]);

  const setPeriodo = (inicio: string, fin: string) => {
    setFechaInicio(inicio);
    setFechaFin(fin);
  };

  const setPeriodoCallCenter = (inicio: string, fin: string) => {
    setFechaInicioCallCenter(inicio);
    setFechaFinCallCenter(fin);
  };

  const guardarPatronHome = (patron: PatronPeriodo) => {
    setPatronPersonalizadoHome(patron);
  };

  const guardarPatronCallCenter = (patron: PatronPeriodo) => {
    setPatronPersonalizadoCallCenter(patron);
  };

  const eliminarPatronHome = () => {
    setPatronPersonalizadoHome(null);
  };

  const eliminarPatronCallCenter = () => {
    setPatronPersonalizadoCallCenter(null);
  };

  // Limpiar run_id cuando cambia el período
  useEffect(() => {
    setCurrentRunId(undefined);
    setTaskId(null);
    setIsCalculating(false);
    setCanalesCalculados([]);
  }, [fechaInicio, fechaFin]);

  return (
    <PeriodoContext.Provider
      value={{
        fechaInicio,
        fechaFin,
        setPeriodo,
        fechaInicioCallCenter,
        fechaFinCallCenter,
        setPeriodoCallCenter,
        patronPersonalizadoHome,
        patronPersonalizadoCallCenter,
        guardarPatronHome,
        guardarPatronCallCenter,
        eliminarPatronHome,
        eliminarPatronCallCenter,
        currentRunId,
        setCurrentRunId,
        taskId,
        setTaskId,
        isCalculating,
        setIsCalculating,
        canalesCalculados,
        setCanalesCalculados,
      }}
    >
      {children}
    </PeriodoContext.Provider>
  );
}

export function usePeriodo() {
  const context = useContext(PeriodoContext);
  if (context === undefined) {
    throw new Error('usePeriodo debe ser usado dentro de un PeriodoProvider');
  }
  return context;
}
